import React from 'react';
import PropTypes from 'prop-types';
import Item from './Item';
import Placeholder from './Placeholder';
import Spinner from '@ux/spinner';
import services from '../utils/services';
import { Events } from '../utils/constants';
import Expander from './Expander';
import { formatPrice } from '../utils/helpers';
import { FormattedMessage } from 'react-intl';
import FreeSearch from '@px/free-search';
import { Text } from '@px/react-utils';

class CartContent extends React.Component {
  constructor(props) {
    super(...arguments);

    this.element = React.createRef();
    this.state = {
      editing: false,
      placeholders: 0,
      basketItems: props.sortedBasketItems || [],
      offsetTop: null,
      height: null,
      restrictedDomains: null
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleFreeSearch = this.handleFreeSearch.bind(this);
    this.handleAddItemEvent = this.handleAddItemEvent.bind(this);
    this.handleRemoveEvent = this.handleRemoveEvent.bind(this);
    this.handleAddPendingEvent = this.handleAddPendingEvent.bind(this);
    this.handleRefreshEvent = this.handleRefreshEvent.bind(this);
  }

  componentDidMount() {
    document.addEventListener(Events.ADD_ITEM, this.handleAddItemEvent);
    document.addEventListener(Events.REMOVE_ITEM, this.handleRemoveEvent);
    document.addEventListener(Events.ADD_PENDING, this.handleAddPendingEvent);
    document.addEventListener(Events.REFRESH, this.handleRefreshEvent);
    this.props.onChange && services.queue.addListener('empty', this.props.onChange);
  }

  componentWillUnmount() {
    document.removeEventListener(Events.ADD_ITEM, this.handleAddItemEvent);
    document.removeEventListener(Events.REMOVE_ITEM, this.handleRemoveEvent);
    document.removeEventListener(Events.ADD_PENDING, this.handleAddPendingEvent);
    document.removeEventListener(Events.REFRESH, this.handleRefreshEvent);
    this.props.onChange && services.queue.removeListener('empty', this.props.onChange);
  }

  handleAddItemEvent() {
    this.handleAddPendingEvent();
    this.handleRefreshEvent();
  }

  handleRemoveEvent(e) {
    !e.detail && this.props.onChange && this.props.onChange();
  }

  handleAddPendingEvent() {
    this.setState({ placeholders: this.state.placeholders + 1 });
  }

  handleRefreshEvent() {
    this.props.onChange && this.props.onChange();
  }

  handleChange(itemOrGroup, update) {
    this.setState({ editing: true });
    services.basket.update(update);
    if (update.type === 'delete') {
      if (itemOrGroup.itemId) {
        const basketItems = this.state.basketItems.filter(({ basketItem }) => basketItem?.itemId !== itemOrGroup.itemId);
        setImmediate(() => this.setState({ basketItems }));
      } else if (itemOrGroup.groupKey)  {
        const basketItems = this.state.basketItems.filter(({ basketItemGroup }) => basketItemGroup?.groupKey !== itemOrGroup.groupKey);
        setImmediate(() => this.setState({ basketItems }));
      }
      document.dispatchEvent(new CustomEvent(Events.REMOVE_ITEM, { detail: itemOrGroup }));
    }
  }

  handleFreeSearch(resultForCart) {
    this.handleAddPendingEvent();
    services.basket.patch(resultForCart.data.cart);
  }

  render() {
    const { basketItems, editing, placeholders } = this.state;
    const { basket, loading, market, currency, language, hasRestrictedDomains } = this.props;
    const isBasketEmpty = !hasRestrictedDomains && (!basketItems || !basketItems.length);

    if (isBasketEmpty && !placeholders) {
      return (
        <div className='hc-loading'>
          { loading ? <Spinner /> : <FormattedMessage id='emptyCart' /> }
        </div>
      );
    }

    return (
      <React.Fragment>
        <div className='hc-summary'>
          <Text size='medium'><FormattedMessage id='yourItems' /></Text>
          <div>
            <span className='p-xs text-muted'>
              <FormattedMessage id='subTotalPrefix' />
            </span>&nbsp;
            <span className='text-primary-o'>
              <strong>
                { editing || placeholders > 0 || isBasketEmpty
                  ? <Spinner inline size='md' />
                  : basket.pricing && formatPrice(market, currency, basket.pricing.subtotal)
                }
              </strong>
            </span>
          </div>
        </div>

        <div className='hc-items'>
          <Expander>
            { hasRestrictedDomains &&
              <div className='ux-card hc-msg'>
                <div>
                  <strong>
                    <FormattedMessage id='restrictedHeader' />
                  </strong>
                </div>
                <div>
                  <FormattedMessage id='restrictedMessage' />
                </div>
              </div>
            }
            <div className='free-search'>
              <FreeSearch
                cart={ basket }
                currency={ currency }
                eid='gce.header_cart.sales_header'
                language={ language.freeSearch }
                marketId={ market } privateLabelId={ 1 }
                updateCart={ this.handleFreeSearch }
              />
            </div>
            {Array(placeholders).fill(0).map((_, index) => <Placeholder key={ index } />)}
            { basketItems && basketItems.map((basketItem, index) =>
              basketItem.basketItem
                ? <Item
                  key={ index }
                  item={ basketItem.basketItem }
                  onChange={ (update) => this.handleChange(basketItem.basketItem, update) }
                />
                : <Item
                  key={ index }
                  item={ basketItem.basketItemGroup }
                  onChange={ (update) => this.handleChange(basketItem.basketItemGroup, update) }
                />
            ) }
          </Expander>
        </div>
      </React.Fragment>
    );
  }
}

CartContent.propTypes = {
  currency: PropTypes.string.isRequired,
  market: PropTypes.string.isRequired,
  language: PropTypes.object.isRequired,
  basket: PropTypes.object,
  loading: PropTypes.bool,
  onChange: PropTypes.func,
  placeholders: PropTypes.number,
  hasRestrictedDomains: PropTypes.bool,
  sortedBasketItems: PropTypes.array
};

export default CartContent;
